// Group-chat data layer (one room per loan phase = loans.loan_id). Mirrors threads.ts:
// polling-based, all access through apiClient, mock/real swap via env.useMocks.
//
// Backend contracts (see docs/SPEC-gruppechat.md, src/worker/group.ts):
//   GET  /group/rooms                          → { rooms: GroupRoom[] }
//   GET  /group/rooms/:loanId/messages?since=N → { messages: GroupMessage[] }  (marks read)
//   POST /group/rooms/:loanId/messages { body }→ { message: { id } }
//     · 404 = not a member · 403 { reason: "optin-required" } = member without opt-in
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@/lib/apiClient";
import { env } from "@/lib/env";
// Reuse the existing chat formatters so group + 1:1 chat render identically.
import { formatAmount, messageClock } from "./threads";

export { formatAmount, messageClock };

// --- backend shapes (identical to the worker side, see SPEC §4) -------------

export interface GroupRoom {
  loanId: number;
  title: string; // loans.address, fallback "Lån #<loanId>"
  address: string | null;
  amount: number | null;
  optInMemberCount: number; // participants with opt-in = 1
  lastMessageAt: string | null;
  preview: string | null;
  unread: number;
}

export interface GroupMessage {
  id: number;
  loanId: number;
  senderId: number; // sender_oblinor_id
  senderRole: "investor" | "loaner";
  senderName: string | null; // resolved server-side (multi-party room)
  body: string;
  createdAt: string; // "YYYY-MM-DD HH:MM:SS" (UTC)
}

// --- formatting helpers (room-list relative time, mirrors threads.ts) -------

/** D1 datetime('now') yields naive UTC ("2026-06-17 09:01:19") — parse as UTC. */
function parseUtc(s: string): Date {
  return new Date(s.replace(" ", "T") + "Z");
}

/** Short relative label for the room list ("09:48", "i går", "12.06"). */
export function roomTimeLabel(s: string | null): string {
  if (!s) return "";
  const d = parseUtc(s);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) {
    return d.toLocaleTimeString("nb-NO", { hour: "2-digit", minute: "2-digit" });
  }
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return "i går";
  return d.toLocaleDateString("nb-NO", { day: "2-digit", month: "2-digit" });
}

// --- mock layer (env.useMocks; same pattern as kunngjoringer.ts) ------------

const mockRooms: GroupRoom[] = [
  {
    loanId: 142,
    title: "Storgata 14, Oslo",
    address: "Storgata 14, Oslo",
    amount: 4500000,
    optInMemberCount: 5,
    lastMessageAt: "2026-06-29 09:41:00",
    preview: "Enig — vi venter på takstrapporten før utbetaling.",
    unread: 2,
  },
  {
    loanId: 87,
    title: "Fjellveien 3, Bergen",
    address: "Fjellveien 3, Bergen",
    amount: 2750000,
    optInMemberCount: 3,
    lastMessageAt: "2026-06-28 16:12:00",
    preview: "Takk for oppdateringen.",
    unread: 0,
  },
];

const mockMessages: Record<number, GroupMessage[]> = {
  142: [
    {
      id: 1,
      loanId: 142,
      senderId: 11,
      senderRole: "loaner",
      senderName: "Pro Bolig AS",
      body: "Hei alle sammen. Fremdriftsplanen er oppdatert og lastet opp.",
      createdAt: "2026-06-29 09:30:00",
    },
    {
      id: 2,
      loanId: 142,
      senderId: 42,
      senderRole: "investor",
      senderName: "Ole Pedersen",
      body: "Ser bra ut. Når forventer dere ferdigstillelse av første byggetrinn?",
      createdAt: "2026-06-29 09:38:00",
    },
    {
      id: 3,
      loanId: 142,
      senderId: 11,
      senderRole: "loaner",
      senderName: "Pro Bolig AS",
      body: "Enig — vi venter på takstrapporten før utbetaling.",
      createdAt: "2026-06-29 09:41:00",
    },
  ],
  87: [
    {
      id: 4,
      loanId: 87,
      senderId: 9,
      senderRole: "loaner",
      senderName: "Veien Hjem AS",
      body: "Takk for oppdateringen.",
      createdAt: "2026-06-28 16:12:00",
    },
  ],
};

// --- queries ----------------------------------------------------------------

/** Rooms the user participates in (only when their own opt-in = 1 → else empty).
 *  Room list polls infrequently (window focus / 30s), per FRONTEND.md §Polling. */
export function useGroupRooms() {
  return useQuery({
    queryKey: ["group", "rooms"],
    queryFn: async () => {
      if (env.useMocks) return mockRooms;
      const r = await apiClient.get<{ ok: true; rooms: GroupRoom[] }>("/group/rooms");
      return r.rooms;
    },
    refetchInterval: 30000,
    refetchIntervalInBackground: false,
  });
}

/** Messages for the open room; polls on a short interval (only the open room). */
export function useGroupMessages(loanId: number | null) {
  return useQuery({
    queryKey: ["group", "messages", loanId],
    enabled: loanId != null,
    queryFn: async () => {
      if (env.useMocks) return mockMessages[loanId as number] ?? [];
      const r = await apiClient.get<{ ok: true; messages: GroupMessage[] }>(
        `/group/rooms/${loanId}/messages?since=0`
      );
      return r.messages;
    },
    refetchInterval: 6000,
    refetchIntervalInBackground: false,
  });
}

// --- mutations --------------------------------------------------------------

export function useSendGroupMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ loanId, body }: { loanId: number; body: string }) => {
      if (env.useMocks) return Date.now();
      const r = await apiClient.post<{ ok: true; message: { id: number } }>(
        `/group/rooms/${loanId}/messages`,
        { body }
      );
      return r.message.id;
    },
    onSuccess: (_id, { loanId }) => {
      qc.invalidateQueries({ queryKey: ["group", "messages", loanId] });
      qc.invalidateQueries({ queryKey: ["group", "rooms"] });
    },
  });
}
